import '@eeacms/volto-tabs-block/less/menu.less';

import React, { useEffect, useState } from 'react';
import { withScrollToTarget } from '@eeacms/volto-tabs-block/hocs';
import { getMenuPosition } from '@eeacms/volto-tabs-block/utils';
import Image from '@plone/volto/components/theme/Image/Image';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';
import cx from 'classnames';
import noop from 'lodash/noop';
import { useIntl } from 'react-intl';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import { Icon } from 'semantic-ui-react';

import messages from '../../../../../../messages/tabs';

// Acima deste total de blocos, os painéis inativos só são montados quando a aba
// é visitada, evitando renderizar megabytes de conteúdo de uma só vez.
const MAX_EAGER_BLOCKS = 100;

// Percorre os blocos aninhados e para assim que ultrapassa o limite.
const countBlocks = (container, limit) => {
  let total = 0;
  const pending = [container];

  while (pending.length && total <= limit) {
    const current = pending.pop();
    const blocks = current?.blocks;
    if (!blocks) continue;

    for (const key of Object.keys(blocks)) {
      total += 1;
      if (total > limit) break;

      const child = blocks[key];
      if (child && typeof child === 'object') {
        if (child.blocks) pending.push(child);
        if (child.data?.blocks) pending.push(child.data);
      }
    }
  }

  return total;
};

export const AssetTab = ({ props, tabIndex, tabTitle }) => {
  const {
    icon,
    image,
    assetType,
    assetPosition,
    iconSize,
    imageSize,
    hideTitle,
  } = props;
  const imageObject = image?.[0];
  return (
    <div
      className={cx('asset-position', {
        'asset-top': assetPosition === 'top',
        'asset-left': assetPosition === 'left',
        'asset-right': assetPosition === 'right',
      })}
    >
      {assetType === 'icon' && icon && (
        <Icon
          className={cx('tab-icon', icon, iconSize, 'aligned')}
          {...{
            ...(hideTitle && {
              role: 'img',
              'aria-hidden': 'false',
              'aria-label': tabTitle,
            }),
          }}
        />
      )}

      {assetType === 'image' && imageObject && (
        <Image
          src={
            isInternalURL(imageObject['@id'])
              ? `${flattenToAppURL(imageObject['@id'])}/${
                  imageObject?.image_scales?.image?.[0].scales?.[imageSize]
                    ?.download || imageObject?.image_scales?.image?.[0].download
                }`
              : imageObject['@id']
          }
          className={`ui image ${imageSize} aligned`}
          alt={hideTitle ? tabTitle : ''}
        />
      )}

      {!hideTitle && (
        <div>
          <span className="menu-item-count">{tabIndex}</span>
          <span className="menu-item-text">{tabTitle}</span>
        </div>
      )}
    </div>
  );
};

const TabItem = (props) => {
  const { activeTab = null, tabs = {}, setActiveTab = noop, blockId } = props;
  const intl = useIntl();

  const { tab, index } = props;
  const tabIndex = index + 1;
  const [tabChanged, setTabChanged] = useState(false);
  const defaultTitle = intl.formatMessage(messages.TabNum, { index: tabIndex });
  const tabSettings = tabs[tab];
  const { title } = tabSettings;
  const tabTitle = title || defaultTitle;
  const tabButtonId = `tab-${tab}`;
  const tabPanelId = `tab-pane-${tab}`;

  useEffect(() => {
    if (
      tabChanged === true &&
      document?.getElementById(blockId)?.querySelector('#tab-pane-' + tab)
    ) {
      document
        .getElementById(blockId)
        .querySelector('#tab-pane-' + tab)
        .focus();
      setTabChanged(false);
    }
  }, [tabChanged, tab, blockId]);

  return (
    <li
      className={cx('tab-item', { active: tab === activeTab })}
      role="presentation"
    >
      <button
        type="button"
        name={defaultTitle}
        id={tabButtonId}
        tabIndex={tab === activeTab ? 0 : -1}
        role="tab"
        aria-selected={tab === activeTab}
        aria-controls={tabPanelId}
        onClick={() => {
          if (activeTab !== tab) {
            setActiveTab(tab);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (activeTab !== tab) {
              setActiveTab(tab);
            }
            setTabChanged(true);
          }
        }}
      >
        <span className="name">{tabTitle}</span>
      </button>
    </li>
  );
};

const View = (props) => {
  const {
    metadata = {},
    data = {},
    tabsList = [],
    tabs = {},
    activeTab,
    renderAllTabs = false,
  } = props;
  const [menuPosition, setMenuPosition] = React.useState({});
  const [visitedTabs, setVisitedTabs] = React.useState({});
  React.useEffect(() => {
    if (Object.keys(menuPosition).length === 0) {
      setMenuPosition(getMenuPosition(data));
    }
  }, [data, menuPosition]);

  // O resultado depende apenas dos dados, então servidor e cliente concordam e
  // a hidratação não quebra.
  const isLargeContent = React.useMemo(
    () => countBlocks({ blocks: tabs }, MAX_EAGER_BLOCKS) > MAX_EAGER_BLOCKS,
    [tabs],
  );

  React.useEffect(() => {
    if (!isLargeContent || !activeTab) return;
    setVisitedTabs((previous) =>
      previous[activeTab] ? previous : { ...previous, [activeTab]: true },
    );
  }, [activeTab, isLargeContent]);

  const { title, description } = data;

  const panes = tabsList.map((tab, index) => {
    const tabTitle = tabs[tab]?.title || `Tab ${tabsList.indexOf(tab) + 1}`;
    const tabPanelId = `tab-pane-${tab}`;
    const tabButtonId = `tab-${tab}`;
    const isActive = tab === activeTab;
    const shouldRenderPane =
      renderAllTabs ||
      (!isLargeContent ? isActive : isActive || !!visitedTabs[tab]);

    return {
      id: tab,
      title: tabTitle,
      tabItem: (
        <TabItem
          {...props}
          key={tab}
          tab={tab}
          index={index}
          tabsTitle={title}
          tabsDescription={description}
          blockId={props?.id || ''}
        />
      ),
      pane: (
        <div
          key={tab}
          className={cx('tab-panel', { active: tab === activeTab })}
          id={tabPanelId}
          role="tabpanel"
          aria-labelledby={tabButtonId}
          aria-hidden={!isActive}
        >
          {shouldRenderPane ? (
            <RenderBlocks {...props} metadata={metadata} content={tabs[tab]} />
          ) : null}
        </div>
      ),
    };
  });

  return (
    <div className="br-tab">
      <nav className="tab-nav">
        <ul role="tablist">{panes.map((pane) => pane.tabItem)}</ul>
      </nav>
      <div className="tab-content">{panes.map((pane) => pane.pane)}</div>
    </div>
  );
};

export default compose(withScrollToTarget)(withRouter(View));
